/**
 * JS-side cache of the current owner window label and epoch.
 *
 * Fed by the `denker://owner-changed` Tauri event. Used by the
 * mirrored-store middleware, use-state-mirror, and use-state-owner
 * so there is one place to read/update owner state instead of
 * duplicating the subscription in every module.
 *
 * Also caches this window's own label (resolved once to avoid
 * await-interleave races across rapid event arrivals).
 *
 * `use-window-role.ts` re-exports `getWindowRoleSync` / `useWindowRole`
 * and continues to work unchanged — it independently subscribes to the
 * same `denker://owner-changed` event.  These two modules are intentionally
 * kept separate so use-window-role stays a thin, UI-focused hook.
 */

import { listen } from "@tauri-apps/api/event";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { resetOwnerSeq } from "@/lib/owner-seq";
import { isTauri } from "@/lib/is-tauri";

// ── My label (this window) ────────────────────────────────────────
// Resolved once at module load to avoid await-interleave races.
// getCurrentWindow().label is synchronous (returns the label stored
// in the Tauri window object — not an async IPC call). Outside Tauri
// (web frontend / OAuth popup) it returns undefined, so guard with
// isTauri() to keep the module browser-safe.
const _myLabel: string = isTauri() ? getCurrentWindow().label : "";

export function getMyWindowLabel(): string {
  return _myLabel;
}

// ── Owner tracking ────────────────────────────────────────────────

// Seed from the synchronous init-script globals stamped in Rust before
// any window's JS runs (`lib.rs` and `display_watcher.rs`). Without
// this seed mirrors miss the very first `denker://owner-changed` event
// (the listener registers after Rust has already emitted) and every
// subsequent `mirror-event` is rejected by the
// `e.from === currentOwnerLabel` filter — the wrench would stay forever.
const _initialOwnerLabel: string | null =
  typeof (window as { __DENKER_OWNER_LABEL__?: unknown }).__DENKER_OWNER_LABEL__ === "string"
    ? ((window as { __DENKER_OWNER_LABEL__?: string }).__DENKER_OWNER_LABEL__ as string)
    : null;
const _initialOwnerEpoch: number =
  typeof (window as { __DENKER_OWNER_EPOCH__?: unknown }).__DENKER_OWNER_EPOCH__ === "number"
    ? ((window as { __DENKER_OWNER_EPOCH__?: number }).__DENKER_OWNER_EPOCH__ as number)
    : 0;

/** Epoch from the most recent `denker://owner-changed` event. */
let _ownerEpoch = _initialOwnerEpoch;

/** Label of the current owner (null = not yet received an event). */
let _ownerLabel: string | null = _initialOwnerLabel;

/** Callbacks subscribed via `subscribeOwnerChanged`. */
type OwnerChangedPayload = { label: string | null; epoch: number };
const _ownerSubs = new Set<(p: OwnerChangedPayload) => void>();

// Subscribe to owner-changed at module load so any module that imports
// this file benefits from the bookkeeping immediately. Skip outside Tauri
// — there are no owner-changed events in the web frontend.
if (isTauri()) {
  void listen<OwnerChangedPayload>("denker://owner-changed", (e) => {
    const { label, epoch } = e.payload;
    _ownerEpoch = epoch;
    _ownerLabel = label;

    // If this window just became owner, reset the sequence counter so the
    // new owner-session starts from 1.
    if (label === _myLabel) {
      resetOwnerSeq();
    }

    _ownerSubs.forEach((cb) => cb(e.payload));
  });
}

/** The epoch from the most recent `denker://owner-changed` event.
 *  0 before the first event arrives. */
export function getCurrentOwnerEpoch(): number {
  return _ownerEpoch;
}

/** The current owner's window label, or null before the first event. */
export function getCurrentOwnerLabel(): string | null {
  return _ownerLabel;
}

/**
 * Subscribe to owner-changed events.  Returns an unsubscribe function.
 * Useful for non-React code that needs to react to ownership changes.
 */
export function subscribeOwnerChanged(
  cb: (p: OwnerChangedPayload) => void,
): () => void {
  _ownerSubs.add(cb);
  return () => _ownerSubs.delete(cb);
}
